import React, { Suspense, lazy, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeftRight, ArrowRight, BarChart3, Compass, Droplets, GitCompareArrows } from 'lucide-react'

import { buildExplorePath } from '@/config/explore'
import { BRIDGE_SITE_URL } from '@/constants/bridgeLink'
import { MarketDataEmpty, MarketDataSkeleton } from '@/data/marketApi/components/MarketDataStates'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { PairInstrumentLogos } from '@/features/market/PairInstrumentLogos'
import { useNutrimaticMarket } from '@/features/market/NutrimaticMarketProvider'
import { buildPoolHrefFromListing } from '@/features/market/nutrimaticCta'
import {
  candleSeriesToMarketCandles,
  formatPoolRowMetrics,
} from '@/features/market/nutrimaticFormat'
import type { NutrimaticPoolRow } from '@/features/market/useNutrimaticMarketHub'
import { cn } from '@/lib/utils'
import { MobileSection } from '@/mobile'

const ChartPanel = lazy(() =>
  import('@/widgets/market/ChartPanel').then((module) => ({ default: module.ChartPanel })),
)

function ChartPanelFallback() {
  return <MarketDataSkeleton className="h-[210px]" />
}

function CircularMover({
  change,
  label,
  item,
}: {
  change: string
  label: string
  item: NutrimaticPoolRow
}) {
  const positive = change.startsWith('+')
  const pairLabel =
    item.listing.pairLabel || `${item.base?.symbol ?? '?'}/${item.quote?.symbol ?? '?'}`
  return (
    <Link
      to={buildPoolHrefFromListing(item.listing, item.poolAddress)}
      className="mobile-app__card flex w-36 shrink-0 snap-start flex-col items-center gap-2 p-3.5 text-center transition-opacity hover:opacity-90 sm:w-40"
    >
      <PairInstrumentLogos base={item.base} quote={item.quote} size={40} layout="stacked" />
      <div
        className={cn(
          'font-mono text-2xl font-semibold tabular-nums tracking-tighter',
          positive ? 'text-profit' : 'text-loss',
        )}
      >
        {change}
      </div>
      <div className="space-y-0.5">
        <div className="mobile-app__caption font-medium uppercase tracking-[0.06em]">{label}</div>
        <div className="truncate text-xs text-muted-foreground tabular-nums">{pairLabel}</div>
      </div>
    </Link>
  )
}

function HotPairTile({ item }: { item: NutrimaticPoolRow }) {
  const t = useTranslation()
  const metrics = formatPoolRowMetrics(item.view)
  const href = buildPoolHrefFromListing(item.listing, item.poolAddress)
  const positive = metrics.change24h.startsWith('+')
  const label =
    item.listing.pairLabel || `${item.base?.symbol ?? '?'}/${item.quote?.symbol ?? '?'}`

  return (
    <Link
      to={href}
      className="mobile-app__card flex w-40 shrink-0 snap-start flex-col gap-3 p-3.5 transition-opacity hover:opacity-90 sm:w-44"
    >
      <div className="flex flex-col items-center gap-2 pt-1">
        <PairInstrumentLogos base={item.base} quote={item.quote} size={36} layout="stacked" />
        <div className="w-full truncate text-center text-sm font-medium tabular-nums tracking-tight">
          {label}
        </div>
      </div>
      <div className="font-mono text-center text-xl tabular-nums tracking-tighter">{metrics.price}</div>
      <div className="mt-auto flex items-end justify-between text-xs">
        <div className={cn('font-mono tabular-nums', positive ? 'text-profit' : 'text-loss')}>
          {metrics.change24h}
        </div>
        <div className="text-muted-foreground tabular-nums">{t.common.volPrefix(metrics.volume24h)}</div>
      </div>
    </Link>
  )
}

function QuickActionTile({
  label,
  sub,
  href,
  icon: Icon,
  accent,
  external,
}: {
  label: string
  sub: string
  href: string
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
  accent?: boolean
  external?: boolean
}) {
  const className = cn(
    'mobile-app__card group flex min-h-[5.5rem] flex-col justify-between p-4 transition-opacity hover:opacity-90',
    accent && 'ring-1 ring-primary/30',
  )
  const content = (
    <>
      <Icon className="size-5 text-primary" aria-hidden />
      <div>
        <div className="mobile-app__body-text font-semibold">{label}</div>
        <div className="mt-1 mobile-app__caption">{sub}</div>
      </div>
    </>
  )

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {content}
      </a>
    )
  }

  return (
    <Link to={href} className={className}>
      {content}
    </Link>
  )
}

function HomePulseChart() {
  const t = useTranslation()
  const { poolRows, poolRowsLoading, pairsLoading, selected, setSelected, detail } =
    useNutrimaticMarket()
  const primary = poolRows[0]

  useEffect(() => {
    if (primary && selected?.venueId !== primary.listing.venueId) {
      setSelected(primary.listing)
    }
  }, [primary, selected?.venueId, setSelected])

  const candles = candleSeriesToMarketCandles(detail.chart)

  if (pairsLoading || poolRowsLoading || (primary && detail.loading && !candles.length)) {
    return <ChartPanelFallback />
  }

  if (!candles.length) {
    return (
      <div className="mobile-app__info-panel">
        <MarketDataEmpty message={t.homeDashboard.noPrimaryPairPrice} />
      </div>
    )
  }

  return (
    <div className="browse-detail__chart-panel">
      <Suspense fallback={<ChartPanelFallback />}>
        <ChartPanel candles={candles} embedded height={210} title={t.homeDashboard.liveMarket} />
      </Suspense>
    </div>
  )
}

export function HomeDashboardContent() {
  const t = useTranslation()
  const { poolRows, poolRowsLoading, pairsLoading } = useNutrimaticMarket()
  const isLoading = pairsLoading || poolRowsLoading
  const pairs = poolRows.slice(0, 6)
  const topMover = pairs[0]
  const topMetrics = topMover ? formatPoolRowMetrics(topMover.view) : null

  const quickActions = useMemo(
    () => [
      {
        label: t.homeDashboard.quickAction.swap,
        sub: t.homeDashboard.quickAction.swapSub,
        href: '/swap',
        icon: ArrowLeftRight,
        accent: true,
      },
      {
        label: t.homeDashboard.quickAction.exploreTokens,
        sub: t.homeDashboard.quickAction.exploreTokensSub,
        href: buildExplorePath('tokens'),
        icon: Compass,
      },
      {
        label: t.homeDashboard.quickAction.explorePools,
        sub: t.homeDashboard.quickAction.explorePoolsSub,
        href: buildExplorePath('pools'),
        icon: Droplets,
      },
      {
        label: t.homeDashboard.quickAction.markets,
        sub: t.homeDashboard.quickAction.marketsSub,
        href: buildExplorePath('markets'),
        icon: BarChart3,
      },
      {
        label: t.homeDashboard.quickAction.bridge,
        sub: t.homeDashboard.quickAction.bridgeSub,
        href: BRIDGE_SITE_URL,
        icon: GitCompareArrows,
        external: true,
      },
    ],
    [t],
  )

  return (
    <div id="platform-overview" className="browse-page--desktop scroll-mt-6 space-y-8 sm:space-y-10">
      <MobileSection title={t.homeDashboard.liveMarket} subtitle={t.homeDashboard.liveMarketSubtitle}>
        <HomePulseChart />
      </MobileSection>

      <MobileSection title={t.homeDashboard.activePairs} subtitle={t.homeDashboard.activePairsSubtitle}>
        {isLoading ? (
          <MarketDataSkeleton className="h-32" />
        ) : !pairs.length ? (
          <MarketDataEmpty />
        ) : (
          <>
            <div className="flex gap-3 overflow-x-auto snap-x pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {topMover && topMetrics ? (
                <CircularMover
                  change={topMetrics.change24h}
                  label={t.homeDashboard.topMover}
                  item={topMover}
                />
              ) : null}
              {pairs.map((item) => (
                <HotPairTile key={item.listing.venueId} item={item} />
              ))}
              <Link
                to={buildExplorePath('markets')}
                className="mobile-app__card flex w-28 shrink-0 snap-start flex-col items-center justify-center p-3 text-center transition-opacity hover:opacity-90 sm:w-32"
              >
                <div className="mobile-app__caption font-medium uppercase tracking-[0.06em]">
                  {t.common.explore}
                </div>
                <div className="mobile-app__body-text font-semibold">{t.homeDashboard.exploreAllPairs}</div>
                <div className="mt-1 mobile-app__caption">{t.homeDashboard.exploreVolume}</div>
              </Link>
            </div>
            <Link
              to={buildExplorePath('markets')}
              className="mt-3 inline-block text-sm text-primary underline-offset-2 hover:underline"
            >
              {t.homeDashboard.viewAllMarkets}
            </Link>
          </>
        )}
      </MobileSection>

      <MobileSection title={t.homeDashboard.quickAccess} subtitle={t.homeDashboard.quickAccessSubtitle}>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {quickActions.map((action) => (
            <QuickActionTile key={action.label} {...action} />
          ))}
        </div>
      </MobileSection>

      <MobileSection title={t.portfolio.sectionTitle} subtitle={t.portfolio.sectionSubtitle}>
        <div className="mobile-app__info-panel">
          <p className="mobile-app__info-kicker">{t.nav.portfolio}</p>
          <p className="mobile-app__hero-metric">—</p>
          <p className="mobile-app__caption">{t.portfolio.connectForAggregated}</p>
          <Link to="/portfolio" className="mobile-app__detail-action mt-4 inline-flex">
            {t.portfolio.openPortfolio}
            <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
      </MobileSection>
    </div>
  )
}
